import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

import { api, unwrap } from '../api/client.js';
import { formatDateTime } from '../i18n.js';
import { useMe } from '../session.js';
import { Card, LinkButton, Notice } from './ui.js';

/**
 * The second check location for one site (T-67): how many suspected outages it confirmed and how
 * many false alarms it avoided. Shown only while the second location actually runs (spec §17).
 */
export function LocationsCard({ siteId }: { siteId: string }) {
  const { t, i18n } = useTranslation();
  const timeZone = useMe().data?.account.timezone;
  const locations = useQuery({
    queryKey: ['site', siteId, 'locations'],
    queryFn: () => unwrap(api.GET('/sites/{siteId}/locations', { params: { path: { siteId } } })),
  });
  const l = locations.data;
  if (!l) return null;
  const total = l.confirmed + l.avoided + l.unavailable;
  if (!l.available)
    return l.active ? (
      <div className="mb-4">
        <Notice kind="info">
          {t('locations.planNeeded')}{' '}
          <LinkButton to="/billing" variant="soft" size="sm">
            {t('locations.upgrade')}
          </LinkButton>
        </Notice>
      </div>
    ) : null;
  if (!l.active && total === 0) return null;

  const stats = [
    { key: 'confirmed', value: l.confirmed },
    { key: 'avoided', value: l.avoided },
    ...(l.unavailable > 0 ? [{ key: 'unavailable', value: l.unavailable }] : []),
  ] as const;

  return (
    <Card className="mb-4">
      <h2 className="mb-1 text-lg font-semibold">{t('locations.title')}</h2>
      <p className="mb-4 max-w-prose text-slate-700">{t('locations.intro')}</p>
      {total === 0 ? (
        <p className="text-slate-700">{t('locations.none', { count: l.days })}</p>
      ) : (
        <>
          <p className="mb-2 text-sm text-slate-700">{t('locations.period', { count: l.days })}</p>
          <dl className="mb-4 flex flex-wrap gap-x-8 gap-y-3">
            {stats.map((s) => (
              <div key={s.key} className="flex flex-col">
                <dt className="text-sm text-slate-700">{t(`locations.${s.key}`)}</dt>
                <dd className="text-2xl font-semibold tabular-nums">{s.value}</dd>
              </div>
            ))}
          </dl>
          <h3 className="mb-2 font-semibold">{t('locations.recent')}</h3>
          <ul className="flex flex-col gap-1">
            {l.recent.map((r) => (
              <li key={r.checkedAt} className="flex flex-wrap gap-x-3 text-sm">
                <span className="tabular-nums text-slate-700">
                  {formatDateTime(r.checkedAt, i18n.language, timeZone)}
                </span>
                <span>{t(`locations.verdicts.${r.verdict}`)}</span>
              </li>
            ))}
          </ul>
        </>
      )}
      {!l.active ? <p className="mt-3 text-sm text-slate-700">{t('locations.paused')}</p> : null}
    </Card>
  );
}
